import React from 'react';
import { motion } from 'framer-motion';
import { Users, Cpu, Sparkles, UserPlus, Briefcase, Palette, MessageCircle } from 'lucide-react';

function TeamSection({ content }) {
  const advisoryNeeds = [
    { role: 'Beauty Industry Veteran', description: 'Former salon owner or brand executive', icon: Sparkles },
    { role: 'Marketplace Expert', description: 'Airbnb, LinkedIn, or similar alumni', icon: Users },
    { role: 'B2B SaaS Leader', description: 'Enterprise strategy and customer success', icon: Briefcase },
  ];

  const postFunding = [
    { role: 'Full-Stack Engineer', timing: 'Month 2-3', icon: Cpu },
    { role: 'Product Designer', timing: 'Month 3-4', icon: Palette },
    { role: 'Community Manager', timing: 'Month 4-6', icon: MessageCircle },
  ];

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-lavender/10 border border-accent-lavender/20 rounded-full mb-6">
            <Users className="w-4 h-4 text-accent-lavender" />
            <span className="text-sm text-accent-lavender">Team</span>
          </div>
          <h2 className="section-title mb-4">
            Building with <span className="text-gradient">AI</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Leveraging AI-assisted development to move fast and stay lean.
          </p>
        </motion.div>

        {/* Founder Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card bg-gradient-to-r from-surface-card to-kore-900/30 border-kore-500/30 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-kore-400 to-kore-700 flex items-center justify-center shadow-xl shadow-kore-500/20">
                <Cpu className="w-16 h-16 text-white" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="text-sm text-kore-400 mb-1">Solo Technical Founder</div>
              <h3 className="text-2xl font-bold mb-2">AI-Assisted Development</h3>
              <p className="text-zinc-400 mb-4">
                Building KoreFolio with Claude as the primary development partner. 
                This approach enables rapid iteration, high-quality code output, and the 
                ability to ship a production-ready MVP in 45 days.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="px-3 py-1 bg-kore-500/10 border border-kore-500/20 rounded-full text-sm text-kore-300">
                  Ship fast
                </span>
                <span className="px-3 py-1 bg-accent-mint/10 border border-accent-mint/20 rounded-full text-sm text-green-300">
                  Iterate faster
                </span>
                <span className="px-3 py-1 bg-accent-lavender/10 border border-accent-lavender/20 rounded-full text-sm text-purple-300">
                  Stay lean
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Advisory Needs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent-gold" />
              Advisory Board (Seeking)
            </h3>
            <div className="space-y-4">
              {advisoryNeeds.map((advisor, index) => {
                const Icon = advisor.icon;
                return (
                  <motion.div
                    key={advisor.role}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 p-2 bg-surface-elevated rounded-lg">
                      <Icon className="w-4 h-4 text-kore-400" />
                    </div>
                    <div>
                      <div className="font-medium text-white">{advisor.role}</div>
                      <div className="text-sm text-zinc-500">{advisor.description}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Post-Funding Hires */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-accent-mint" />
              Post-Funding Hires
            </h3>
            <div className="space-y-4">
              {postFunding.map((hire, index) => {
                const Icon = hire.icon;
                return (
                  <motion.div
                    key={hire.role}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 p-2 bg-surface-elevated rounded-lg">
                        <Icon className="w-4 h-4 text-accent-mint" />
                      </div>
                      <span className="font-medium text-white">{hire.role}</span>
                    </div>
                    <span className="text-sm text-zinc-500">{hire.timing}</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default TeamSection;
